import type { FirebaseApp } from 'firebase/app';

// O SDK do Firebase é carregado sob demanda (import dinâmico) para não pesar
// no bundle das páginas públicas; só o admin logado usa tempo real e push.

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'roda-de-premios.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'roda-de-premios',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Sem apiKey/appId o app funciona normalmente, apenas sem tempo real e push.
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.appId);

let appPromise: Promise<FirebaseApp> | null = null;

const getApp = () => {
  if (!appPromise) {
    appPromise = import('firebase/app').then(({ initializeApp }) => initializeApp(firebaseConfig));
  }
  return appPromise;
};

export const loadAuth = async () => {
  const [app, authModule] = await Promise.all([getApp(), import('firebase/auth')]);
  return {
    auth: authModule.getAuth(app),
    signInWithCustomToken: authModule.signInWithCustomToken,
    signOut: authModule.signOut,
  };
};

export const loadFirestore = async () => {
  const [app, firestoreModule] = await Promise.all([getApp(), import('firebase/firestore')]);
  return {
    db: firestoreModule.getFirestore(app),
    doc: firestoreModule.doc,
    onSnapshot: firestoreModule.onSnapshot,
  };
};

let messagingPromise: ReturnType<typeof createMessaging> | null = null;

const createMessaging = async () => {
  try {
    const [app, messagingModule] = await Promise.all([getApp(), import('firebase/messaging')]);
    if (!(await messagingModule.isSupported())) return null;
    return {
      messaging: messagingModule.getMessaging(app),
      getToken: messagingModule.getToken,
      deleteToken: messagingModule.deleteToken,
    };
  } catch {
    return null;
  }
};

export const getMessagingIfSupported = () => {
  if (!isFirebaseConfigured || !VAPID_KEY) return Promise.resolve(null);
  if (!messagingPromise) messagingPromise = createMessaging();
  return messagingPromise;
};
